import { Injectable, DestroyRef, ApplicationRef, inject, effect, signal, computed, untracked } from '@angular/core';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subscription, firstValueFrom, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { AccountGateService } from './account-gate.service';
import { LocationSharingType, SelfDevice, UserSelf } from './user-self.model';

/** Delay between background self re-probes while the account is forbidden (403). */
const ACCOUNT_RETRY_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class UserSelfService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gate = inject(AccountGateService);
  private readonly appRef = inject(ApplicationRef);

  readonly self = httpResource<UserSelf>(
    () => `${environment.vigilantApiUrl}/api/users/self`
  );

  // Devices belong to a resolved account — don't probe them until self resolves. Returning
  // undefined keeps the resource idle, so a forbidden (no-account) login never fires this call
  // alongside the gating self probe.
  readonly devices = httpResource<SelfDevice[]>(() =>
    this.self.status() === 'resolved'
      ? `${environment.vigilantApiUrl}/api/users/self/devices`
      : undefined
  );

  private retrySub: Subscription | null = null;

  /**
   * The resolved self, or null while loading / reloading / errored / forbidden. NEVER throws —
   * unlike `self.value()`, which throws a ResourceValueError in the error state. That throw, when
   * it happens inside an always-on effect or a template binding, aborts the whole change-detection
   * pass and prevented the account overlay from rendering on the first 403. Read THIS everywhere
   * that may run before self resolves.
   */
  readonly value = computed<UserSelf | null>(() => this.self.hasValue() ? this.self.value() : null);

  /**
   * Ids of EVERY realm the caller is a Safety Admin for — not just the realm behind their current
   * default org. A Safety Admin can administer more than one realm at once, and their roster
   * (`SystemUsersService.roster`) includes every user in all of them, regardless of which realm
   * their currently selected group happens to belong to. The HUD's "Safety Admin" badge
   * (`hud-panel.component.ts`) also reads this (non-empty) to explain why extra people are listed.
   */
  readonly safetyAdminRealmIds = computed<Set<number>>(() => {
    const realms = this.value()?.realms ?? [];
    return new Set(realms.filter(r => r.isSafetyAdmin).map(r => r.id));
  });

  /** Ids of every realm the caller administers: realm-level Admin, or Admin of any org within it. */
  readonly adminRealmIds = computed<Set<number>>(() => {
    const self = this.value();
    const ids = new Set<number>();
    for (const r of self?.realms ?? []) {
      if (r.role === 'Admin') ids.add(r.id);
    }
    for (const o of self?.orgs ?? []) {
      if (o.role === 'Admin') ids.add(o.realmId);
    }
    return ids;
  });

  /**
   * Whether the caller administers this org: an Admin of the org itself, or an Admin of the
   * realm it belongs to (realm-level Admin, or Admin of any org within that realm). Drives the
   * "you administer this" shield badge on group/account listings, and whether the caller may
   * create an invite for the org — the API only allows that (returns 404 otherwise) under the
   * same rule.
   */
  isOrgAdmin(org: { id: number; realmId?: number | null }): boolean {
    const selfOrg = (this.value()?.orgs ?? []).find(o => o.id === org.id);
    if (selfOrg?.role === 'Admin') return true;
    return org.realmId != null && this.adminRealmIds().has(org.realmId);
  }

  /**
   * True whenever the self probe is in a 403 "no account" state. Derived DIRECTLY from the
   * resource status/error (not an effect-driven counter) so the recovery overlay shows the moment
   * the very first 403 lands — no dependence on effect scheduling, which raced and swallowed that
   * first, very-fast error. `_sawForbidden` keeps it sticky across the background re-probes (whose
   * transient 'reloading' status would otherwise flicker the overlay off and back on).
   */
  private readonly _sawForbidden = signal(false);
  readonly accountUnavailable = computed(() => {
    if (this._sawForbidden()) return true;
    return this.self.status() === 'error' && isForbidden(this.self.error());
  });

  /**
   * Whether dependent bootstrap (sync loop, alerts, history) may start. False
   * while self is loading and — critically — while it is 403-forbidden, so the
   * initial sync process never runs for an account we can't resolve. Other
   * (e.g. transient connectivity) errors fall through to the prior behaviour.
   */
  readonly canBootstrap = computed(() => {
    const status = this.self.status();
    if (status === 'resolved' || status === 'local') return true;
    if (status === 'error') return !isForbidden(this.self.error());
    return false; // idle | loading | reloading
  });

  // Side-effects of the self status: raise/lower the API-halt gate and run a background re-probe
  // every 60s while forbidden so a server-provisioned account auto-recovers. The overlay's
  // visibility is driven by the accountUnavailable computed above, NOT by this effect, so a 403
  // is never missed if this effect happens to run late. untracked() keeps our writes from
  // re-triggering the effect.
  private readonly _accountWatch = effect(() => {
    const status = this.self.status();
    const error = this.self.error();
    untracked(() => {
      if (status === 'resolved' || status === 'local') {
        this._sawForbidden.set(false);
        this.gate.blocked.set(false);
        this.clearRetry();
        this.forceRepaint();
      } else if (status === 'error' && isForbidden(error)) {
        this.onForbidden();
      }
    });
  });

  /**
   * Force a change-detection pass. The self resource can settle (403 or resolve) while the
   * lazily-loaded app-shell/overlay view isn't actively repainting, so the account-state signals
   * change but nothing re-renders until the next unrelated zone tick (previously the 60s retry).
   * Deferred to a microtask so it never runs re-entrantly inside the current CD/effect.
   */
  private forceRepaint(): void {
    queueMicrotask(() => {
      try { this.appRef.tick(); } catch { /* a CD already in progress will repaint anyway */ }
    });
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.clearRetry());
  }

  private onForbidden(): void {
    // Halt every other API call the moment self is forbidden, and remember it so the overlay
    // stays put across background re-probes.
    this.gate.blocked.set(true);
    this._sawForbidden.set(true);
    // The 403 can land before the lazily-loaded shell subscribes to accountUnavailable — force a
    // CD so the recovery overlay shows on the FIRST 403, not only after a background retry.
    this.forceRepaint();
    // Keep re-probing in the background so an account provisioned server-side shortly after login
    // auto-recovers and tears the overlay down — no user action, no automatic logout.
    if (!this.retrySub) {
      this.retrySub = timer(ACCOUNT_RETRY_MS, ACCOUNT_RETRY_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.self.reload());
    }
  }

  private clearRetry(): void {
    this.retrySub?.unsubscribe();
    this.retrySub = null;
  }

  /**
   * Re-probe api/users/self after the user has recovered their account (accepted an
   * invite or onboarded a new realm). The self GET is always allowed past the gate; on
   * a successful load the account watch resets the attempt counter and clears the gate,
   * which tears down the error overlay. Left at the current attempt count meanwhile so
   * the overlay stays put until self actually resolves.
   */
  recheckAccount(): void {
    this.self.reload();
  }

  deleteDevice(deviceId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.vigilantApiUrl}/api/users/self/devices/${deviceId}`
    );
  }

  patchDefaultOrg(userId: number, defaultOrgId: number, locationSharing: LocationSharingType | null | undefined): Observable<unknown> {
    return this.http.patch(`${environment.vigilantApiUrl}/api/users/${userId}`, {
      usernameProvided: false,
      proxyEmailProvided: false,
      locationSharing: locationSharing ?? 'None',
      defaultOrgId,
    });
  }

  /**
   * Switches the caller's current/home group. Reloads `self` on success so every reader of
   * `defaultOrgId` (the HUD's group switcher, `GroupTableComponent`, etc.) picks up the change.
   * Rejects groups the caller isn't a member of — `AdminStore.visibleOrgs` surfaces every group in
   * a realm an admin administers, including ones they haven't personally joined, and those aren't
   * valid homes. `orgName` is used only to compose that error message.
   *
   * Returns an error message to show the caller, or null on success/no-op.
   */
  async switchDefaultOrg(defaultOrgId: number, orgName?: string | null): Promise<string | null> {
    const user = this.value();
    if (!user || defaultOrgId === user.defaultOrgId) return null;
    if (!(user.orgs ?? []).some(o => o.id === defaultOrgId)) {
      return `Not a member of Group ${orgName ?? `#${defaultOrgId}`}, cannot make this group your default group`;
    }
    try {
      await firstValueFrom(this.patchDefaultOrg(user.id, defaultOrgId, user.locationSharing));
      this.self.reload();
      return null;
    } catch {
      return 'Could not switch your default group. Please try again.';
    }
  }

  readonly avatarUploading = signal(false);
  readonly avatarError = signal<string | null>(null);

  /** Uploads a new avatar image for the caller. Reloads `self` on success so `avatarUrl`
   *  updates everywhere it's displayed. */
  async setAvatar(file: File): Promise<boolean> {
    this.avatarError.set(null);
    this.avatarUploading.set(true);
    const form = new FormData();
    form.append('File', file);
    try {
      // responseType: 'text' — the API's 200/204 response isn't JSON (a bare url string or an
      // empty body), and HttpClient's default json parsing throws on that, turning an already-
      // successful PATCH into a reported failure and skipping the reload() below.
      await firstValueFrom(this.http.patch(`${environment.vigilantApiUrl}/api/users/self/avatar`, form, { responseType: 'text' }));
      this.self.reload();
      return true;
    } catch {
      this.avatarError.set('Could not update avatar.');
      return false;
    } finally {
      this.avatarUploading.set(false);
    }
  }
}

function isForbidden(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 403;
}
